// Property-side operations (pure; tested). Prices are the approved CAD
// pre-tax rack prices; the server prices every booking again (REIT portfolio
// discount, HST) before Stripe Checkout, so these are for display only.
export const SERVICES = {
  pm_listing_set: { name: "Listing set", cents: 19500, scope: "unit", what: "Photos and a floor plan for the listing." },
  pm_listing_video: { name: "Listing set with video", cents: 25500, scope: "unit", what: "Photos, floor plan and a short video." },
  pm_condition_report: { name: "Condition report", cents: 14500, scope: "unit", move: ["move_in", "move_out"], what: "Dated photos of every room at move-in or move-out." },
  pm_turnover: { name: "Turnover", cents: 29500, scope: "unit", move: ["move_out"], what: "Move-out condition report plus listing photos and floor plan in one visit." },
  pm_building_set: { name: "Building set", cents: 65000, scope: "building", what: "Exterior, lobby, amenities and an aerial." },
  cre_leasing_s: { name: "Leasing set, up to 2,500 sq ft", cents: 59500, scope: "space", maxSqft: 2500, what: "Photos, measured floor plan and a Virtual Visit." },
  cre_leasing_m: { name: "Leasing set, up to 7,500 sq ft", cents: 89500, scope: "space", maxSqft: 7500, what: "Photos, measured floor plan and a Virtual Visit." },
  cre_leasing_l: { name: "Leasing set, up to 20,000 sq ft", cents: 145000, scope: "space", maxSqft: 20000, what: "Photos, measured floor plan and a Virtual Visit." },
  cre_aerial: { name: "Aerial set", cents: 34900, scope: "property", drone: true, what: "Aerial photos and video of the site." },
  inv_acquisition: { name: "Acquisition capture", cents: 125000, scope: "property", drone: true, what: "Interior, exterior, aerial and a Virtual Visit: your dated baseline." },
  inv_acquisition_mapped: { name: "Mapped acquisition capture", cents: 225000, scope: "property", drone: true, what: "The acquisition capture plus a measured site map." },
};

const FAMILY = {
  property_manager: ["pm_turnover", "pm_condition_report", "pm_listing_set", "pm_listing_video", "pm_building_set"],
  commercial: ["cre_leasing_s", "cre_leasing_m", "cre_leasing_l", "cre_aerial"],
  investor: ["inv_acquisition", "inv_acquisition_mapped"],
};
const FAMILIES_FOR = {
  property_manager: ["property_manager"],
  commercial: ["commercial"],
  investor: ["investor"],
  reit_leasing: ["property_manager", "commercial", "investor"],
};

// Which side of the business a property is run as. A REIT runs each building
// by its segment; everyone else by their account type.
export function modeFor(kind, segment) {
  if (kind === "reit_leasing") return segment === "residential" ? "property_manager" : segment === "commercial" ? "commercial" : "investor";
  return FAMILY[kind] ? kind : null;
}

export function servicesFor(kind, scope) {
  const keys = (FAMILIES_FOR[kind] || []).flatMap((f) => FAMILY[f]);
  return scope ? keys.filter((k) => (scope === "unit" ? ["unit", "space"] : ["building", "property"]).includes(SERVICES[k].scope)) : keys;
}

export const money = (cents) => (cents == null ? "" : `$${(cents / 100).toLocaleString("en-CA", { maximumFractionDigits: cents % 100 ? 2 : 0, minimumFractionDigits: cents % 100 ? 2 : 0 })}`);
export const label = (key) => (SERVICES[key] && SERVICES[key].name) || "Visit";

export function tierForSqft(sqft) {
  const n = Number(String(sqft || "").replace(/,/g, ""));
  if (!n || n <= 0) return null;
  if (n <= 2500) return "cre_leasing_s";
  if (n <= 7500) return "cre_leasing_m";
  if (n <= 20000) return "cre_leasing_l";
  return null;
}

export function addDays(iso, n) {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

// Same rules the server applies (common/client_booking.validate), so the form
// can say what's wrong before a round trip. Returns "" when the form is ready.
export function checkBooking(f, today) {
  const s = SERVICES[f.service_key];
  if (!s) return "Pick a service.";
  if (s.scope === "space" && !f.unit) return "Pick the space.";
  if (s.maxSqft) {
    const right = tierForSqft(f.sqft);
    if (!right) return Number(f.sqft) > 20000 ? "Spaces over 20,000 sq ft are quoted. Send us a request." : "Add the space size in square feet.";
    if (right !== f.service_key) return `A ${Number(f.sqft).toLocaleString("en-CA")} sq ft space takes the ${label(right).toLowerCase()}.`;
  }
  const lead = s.drone ? 2 : 1;
  if (s.move) {
    if (!s.move.includes(f.move_kind)) return "Say whether this is for a move-in or a move-out.";
    if (!/^\d{4}-\d{2}-\d{2}$/.test(f.move_date || "")) return "Add the move-in or move-out date.";
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f.preferred_date || "")) return "Pick a visit date.";
  if (f.preferred_date < addDays(today, lead)) return `Pick a date at least ${lead} day${lead > 1 ? "s" : ""} out.`;
  if (f.preferred_date > addDays(today, 180)) return "Pick a date within the next six months.";
  if (s.move) {
    const gap = Math.round((Date.parse(f.preferred_date) - Date.parse(f.move_date)) / 86400000);
    if (f.move_kind === "move_out" && !(gap >= 0 && gap <= 14)) return "A move-out report is shot within two weeks after the move-out date.";
    if (f.move_kind === "move_in" && !(gap >= -14 && gap <= 0)) return "A move-in report is shot within two weeks before the move-in date, while the unit is empty.";
  }
  if (!(f.site_contact_phone || "").trim() && !(f.access_notes || "").trim()) return "Tell us who lets us in (a phone number) or how to get in (lockbox, concierge).";
  return "";
}

// The visit date that fits a move: the day after keys come back, or the day before move-in.
export function defaultVisitDate(serviceKey, moveKind, moveDate, today) {
  const s = SERVICES[serviceKey];
  const lead = s && s.drone ? 2 : 1;
  const earliest = addDays(today, lead);
  if (s && s.move && moveDate) {
    const d = addDays(moveDate, moveKind === "move_in" ? -1 : 1);
    return d < earliest ? earliest : d;
  }
  return earliest;
}

export function bookingBody(f) {
  const s = SERVICES[f.service_key] || {};
  const body = { service_key: f.service_key, preferred_date: f.preferred_date, access_notes: (f.access_notes || "").trim(),
    site_contact_phone: (f.site_contact_phone || "").trim(), contact_phone: (f.contact_phone || "").trim() };
  if (f.lot_id && (s.scope === "unit" || s.scope === "space")) body.lot_id = f.lot_id;
  else if (f.unit && (s.scope === "unit" || s.scope === "space")) body.unit = f.unit;
  if (s.maxSqft) body.sqft = Number(String(f.sqft).replace(/,/g, ""));
  if (s.move) { body.move_kind = f.move_kind; body.move_date = f.move_date; }
  return body;
}

export const SEGMENT = { residential: "Residential", commercial: "Commercial", holdings: "Other holdings" };
export const HUB_COPY = {
  property_manager: { title: "Turnovers", lead: "Every unit, where it is in the turnover and the next step." },
  commercial: { title: "Leasing", lead: "Every space, whether its leasing media is ready, and what to book." },
  investor: { title: "Portfolio", lead: "A dated baseline for every property and the next check due." },
  reit_leasing: { title: "Portfolio", lead: "Residential, commercial and holdings in one record, with portfolio pricing." },
};

// Hub figures and lists, from /dev/ops/portfolio plus per-property board or
// leasing views. Everything shown is counted from delivered records.
export function hubModel(kind, portfolio, views = {}) {
  const props = (portfolio && portfolio.properties) || [];
  const name = Object.fromEntries(props.map((x) => [x.project_id, x.name]));
  if (kind === "property_manager") {
    const units = Object.entries(views).flatMap(([pid, b]) => ((b && b.units) || []).map((u) => ({ ...u, project_id: pid, property: name[pid] || "" })));
    const act = units.filter((u) => u.book);
    return {
      stats: [{ label: "Buildings", value: props.length }, { label: "Units", value: units.length },
        { label: "Need a visit", value: act.length, attention: act.length > 0 }],
      action: act, actionTitle: "Units that need a visit",
    };
  }
  if (kind === "commercial") {
    const spaces = Object.entries(views).flatMap(([pid, l]) => ((l && l.spaces) || []).map((x) => ({ ...x, project_id: pid, property: name[pid] || "" })));
    const open = spaces.filter((x) => x.status !== "sold");
    const sqft = open.reduce((n, x) => n + (Number(x.sqft) || 0), 0);
    const todo = open.filter((x) => x.book);
    return {
      stats: [{ label: "Spaces to lease", value: open.length }, { label: "Square feet to lease", value: sqft.toLocaleString("en-CA") },
        { label: "Need leasing media", value: todo.length, attention: todo.length > 0 }],
      action: todo, actionTitle: "Spaces that need leasing media",
    };
  }
  if (kind === "reit_leasing") {
    const r = (portfolio && portfolio.reit) || {};
    const spend = r.spend_ytd_cad_cents != null ? r.spend_ytd_cad_cents : (r.properties || []).reduce((n, x) => n + (x.spend_ytd_cad_cents || 0), 0);
    return {
      stats: [{ label: "Properties", value: r.count || props.length }, { label: "Spent this year", value: money(spend) },
        { label: "Portfolio pricing", value: r.discount_pct ? `${r.discount_pct}% off` : "Standard" }],
      action: [], actionTitle: "",
      tier: r.next_tier_at ? `${r.next_tier_at - (r.count || 0)} more ${r.next_tier_at - (r.count || 0) === 1 ? "property" : "properties"} moves the whole portfolio to ${r.discount_pct >= 10 ? 15 : 10}% off.` : r.discount_pct ? "Your portfolio is on the top pricing tier." : "",
      segments: r.by_segment || {}, reit: r.properties || [],
    };
  }
  const overdue = props.filter((x) => x.overdue);
  const nobase = props.filter((x) => !x.baseline);
  return {
    stats: [{ label: "Properties", value: props.length }, { label: "Without a baseline", value: nobase.length, attention: nobase.length > 0 },
      { label: "Checks overdue", value: overdue.length, attention: overdue.length > 0 }],
    action: [...overdue, ...nobase], actionTitle: "Needs a visit",
  };
}
